// =============================================================================
// features/meu-whatsapp/useConnectPrompt.ts — Estado do convite "conecte o seu
// WhatsApp" (F56-S03).
//
// Duas peças, para que quem não é elegível não pague nem a consulta:
//   - `useConnectPromptEligibility()`: só flag + permissão (barato, cache do
//     app). O componente do convite só monta o resto se isto for `true`.
//   - `useConnectPrompt()`: lê o estado do aparelho com `useMeuPareamento`
//     (a mesma consulta da página /meu-whatsapp, mesma chave de cache) e
//     aplica a regra pura de `connectPrompt.ts`.
//
// Falhas da consulta são o desenho, não erro a exibir: 404 = usuário sem agente
// vinculado; 403 = usuário ainda preso ao "primeiro acesso" (F56-S01). Nos dois
// `data` fica indefinido, `hasAgent` é falso e o convite não abre.
//
// "Travado aberto": depois de abrir, o convite continua aberto mesmo quando o
// status vira WORKING — é o que permite mostrar a tela de sucesso. Quem decide
// fechar então é o componente (`finish`).
// =============================================================================

import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useAuthStore } from '../../lib/auth-store';
import { usePersonalEmailGuard } from '../account/usePersonalEmailGuard';

import {
  CONNECT_PROMPT_FLAG,
  CONNECT_PROMPT_PERMISSION,
  hasDismissedConnectPrompt,
  isConnectPromptRouteExcluded,
  markConnectPromptDismissed,
  shouldShowConnectWhatsAppPrompt,
} from './connectPrompt';
import { useMeuPareamento } from './useMeuWhatsApp';
import type { MeuStatus } from './useMeuWhatsApp';

/** Flag ligada + permissão `channel.self_pair`. Sem rede própria. */
export function useConnectPromptEligibility(): boolean {
  const { enabled: flagEnabled } = useFeatureFlag(CONNECT_PROMPT_FLAG);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  return flagEnabled && hasPermission(CONNECT_PROMPT_PERMISSION);
}

export interface ConnectPromptState {
  /** `true` enquanto o convite deve estar na tela. */
  readonly open: boolean;
  readonly status: MeuStatus | null;
  /** "Depois" / Esc: fecha e dispensa o convite nesta aba do navegador. */
  readonly dismiss: () => void;
  /** Encerramento natural (aparelho conectado): fecha sem dispensar. */
  readonly finish: () => void;
}

/**
 * Só deve ser montado quando `useConnectPromptEligibility()` é `true` (o
 * componente `ConnectWhatsAppPrompt` garante isso), mas aplica a regra
 * completa mesmo assim.
 */
export function useConnectPrompt(): ConnectPromptState {
  const eligible = useConnectPromptEligibility();
  const { pathname } = useLocation();
  const me = useMeuPareamento();
  // Modal obrigatório de 1º login (e-mail pessoal): o convite espera. Mesma
  // consulta do guard do App (chave compartilhada); carregando = pendente.
  const personalEmail = usePersonalEmailGuard();
  const personalEmailPending = personalEmail.isLoading || personalEmail.requiresPersonalEmail;

  const agentId = me.data?.agent_id ?? null;
  const status = me.data?.status ?? null;
  const routeExcluded = isConnectPromptRouteExcluded(pathname);

  // Espelho em memória do "dispensado": vale mesmo se o sessionStorage falhar.
  const [dismissedInMemory, setDismissedInMemory] = React.useState(false);
  const [latched, setLatched] = React.useState(false);

  const dismissed = dismissedInMemory || (agentId !== null && hasDismissedConnectPrompt(agentId));

  const shouldShow = shouldShowConnectWhatsAppPrompt({
    flagEnabled: eligible,
    hasPermission: eligible,
    hasAgent: agentId !== null,
    status,
    pathname,
    dismissed,
    personalEmailPending,
  });

  // Abre quando a regra manda; fecha (sem dispensar) ao entrar numa rota
  // excluída — o QR desmonta e a credencial some. Voltando a uma rota normal
  // sem conectar, a regra reabre.
  React.useEffect(() => {
    if (shouldShow && !latched) setLatched(true);
    else if (latched && routeExcluded) setLatched(false);
  }, [shouldShow, latched, routeExcluded]);

  const dismiss = React.useCallback(() => {
    setDismissedInMemory(true);
    setLatched(false);
    if (agentId !== null) markConnectPromptDismissed(agentId);
  }, [agentId]);

  const finish = React.useCallback(() => {
    setLatched(false);
  }, []);

  return {
    open: latched && !dismissedInMemory && !routeExcluded,
    status,
    dismiss,
    finish,
  };
}
